import { useEffect, useState } from "react";

/* ──────────────────────────────────────────────────────────────────────────
   Shared background scroll lock for overlays (nav drawer, modals, sheets).

   WHY THIS EXISTS
   `overflow: hidden` on <body> does NOT stop touch scrolling on iOS Safari —
   the page keeps scrolling under an open modal/drawer. That was the app-wide
   "you can scroll the page underneath" bug. The robust, cross-browser fix is to
   pin the body with `position: fixed` and a negative top offset, then restore
   the scroll position on release.

   WHY REFERENCE-COUNTED
   Several overlays can be open at once (e.g. a calculator opened from inside the
   mobile nav menu). A naive per-component lock makes the second locker snapshot
   the ALREADY-pinned body as its "previous" state, so unmount order corrupts the
   restore and the page jumps. We pin on the first lock and only release — with
   the original scroll position restored — when the last lock clears.
   ────────────────────────────────────────────────────────────────────────── */

interface BodySnapshot {
  overflow: string;
  position: string;
  top: string;
  left: string;
  right: string;
  width: string;
  overscrollBehavior: string;
}
interface HtmlSnapshot {
  overflow: string;
  overscrollBehavior: string;
}

let lockCount = 0;
let savedScrollY = 0;
let savedBody: BodySnapshot | null = null;
let savedHtml: HtmlSnapshot | null = null;

function pinBody(): void {
  if (typeof document === "undefined") return;
  savedScrollY = window.scrollY;
  const bodyStyle = document.body.style;
  const htmlStyle = document.documentElement.style;

  savedBody = {
    overflow: bodyStyle.overflow,
    position: bodyStyle.position,
    top: bodyStyle.top,
    left: bodyStyle.left,
    right: bodyStyle.right,
    width: bodyStyle.width,
    overscrollBehavior: bodyStyle.overscrollBehavior,
  };
  savedHtml = {
    overflow: htmlStyle.overflow,
    overscrollBehavior: htmlStyle.overscrollBehavior,
  };

  // iOS-safe pin: fixed body offset by the current scroll so the viewport
  // doesn't jump, and overscroll-behavior:none kills rubber-band chaining.
  bodyStyle.overflow = "hidden";
  bodyStyle.position = "fixed";
  bodyStyle.top = `-${savedScrollY}px`;
  bodyStyle.left = "0";
  bodyStyle.right = "0";
  bodyStyle.width = "100%";
  bodyStyle.overscrollBehavior = "none";
  htmlStyle.overflow = "hidden";
  htmlStyle.overscrollBehavior = "none";
}

function unpinBody(): void {
  if (typeof document === "undefined" || !savedBody || !savedHtml) return;
  const bodyStyle = document.body.style;
  const htmlStyle = document.documentElement.style;

  bodyStyle.overflow = savedBody.overflow;
  bodyStyle.position = savedBody.position;
  bodyStyle.top = savedBody.top;
  bodyStyle.left = savedBody.left;
  bodyStyle.right = savedBody.right;
  bodyStyle.width = savedBody.width;
  bodyStyle.overscrollBehavior = savedBody.overscrollBehavior;
  htmlStyle.overflow = savedHtml.overflow;
  htmlStyle.overscrollBehavior = savedHtml.overscrollBehavior;

  savedBody = null;
  savedHtml = null;
  // Restore where the user was before the overlay pinned the body.
  window.scrollTo(0, savedScrollY);
}

/**
 * Locks background scroll while `locked` is true. Safe to call from many
 * overlays simultaneously — the lock is reference-counted, so the body is only
 * released when the last open overlay clears.
 *
 * Gate the argument to control when it applies, e.g. `useScrollLock(open)` for a
 * true modal (locks on every screen size) or `useScrollLock(open && isMobile)`
 * for a sheet/dropdown that should stay scroll-through on desktop.
 */
export function useScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return;
    lockCount += 1;
    if (lockCount === 1) pinBody();
    return () => {
      lockCount -= 1;
      if (lockCount <= 0) {
        lockCount = 0;
        unpinBody();
      }
    };
  }, [locked]);
}

/**
 * LIGHT background scroll lock for non-dominating sheets/dropdowns (notification
 * sheet, admin active-users popover) that are `position: fixed` and cover most of
 * the viewport on mobile.
 *
 * WHY A SEPARATE HOOK (vs. {@link useScrollLock})
 * The heavy lock pins `<body>` with `position: fixed; top: -scrollY` to kill iOS
 * background touch-scroll. That pin has two side effects on iOS Safari that break
 * these dropdown sheets:
 *   1. `position: fixed` descendants (the top nav bar) get pushed off-screen —
 *      the nav bar "disappears" when the sheet opens.
 *   2. The sheet's own internal `overflow-y: auto` body stops accepting touch
 *      scroll — the list won't scroll.
 * The light lock avoids both by ONLY clamping overflow (no `position: fixed`),
 * so the fixed nav stays put and the sheet's own scroll container still works.
 * The sheet itself covers the viewport, so clamping the background's overflow is
 * enough to stop scroll-through on modern iOS (the body pin was only needed for
 * older iOS).
 *
 * It keeps its own reference count + snapshot so it composes safely with the
 * heavy lock (worst case of interleaved open/close is a brief background-scroll
 * window, never a broken nav or a frozen sheet).
 */
let lightCount = 0;
let lightSavedBody: { overflow: string; overscrollBehavior: string } | null = null;
let lightSavedHtml: { overflow: string; overscrollBehavior: string } | null = null;

function lightLock(): void {
  if (typeof document === "undefined") return;
  const bodyStyle = document.body.style;
  const htmlStyle = document.documentElement.style;
  lightSavedBody = {
    overflow: bodyStyle.overflow,
    overscrollBehavior: bodyStyle.overscrollBehavior,
  };
  lightSavedHtml = {
    overflow: htmlStyle.overflow,
    overscrollBehavior: htmlStyle.overscrollBehavior,
  };
  bodyStyle.overflow = "hidden";
  bodyStyle.overscrollBehavior = "none";
  htmlStyle.overflow = "hidden";
  htmlStyle.overscrollBehavior = "none";
}

function lightUnlock(): void {
  if (typeof document === "undefined" || !lightSavedBody || !lightSavedHtml) return;
  const bodyStyle = document.body.style;
  const htmlStyle = document.documentElement.style;
  bodyStyle.overflow = lightSavedBody.overflow;
  bodyStyle.overscrollBehavior = lightSavedBody.overscrollBehavior;
  htmlStyle.overflow = lightSavedHtml.overflow;
  htmlStyle.overscrollBehavior = lightSavedHtml.overscrollBehavior;
  lightSavedBody = null;
  lightSavedHtml = null;
}

export function useScrollLockLight(locked: boolean): void {
  useEffect(() => {
    if (!locked) return;
    lightCount += 1;
    if (lightCount === 1) lightLock();
    return () => {
      lightCount -= 1;
      if (lightCount <= 0) {
        lightCount = 0;
        lightUnlock();
      }
    };
  }, [locked]);
}

/**
 * Reactive "is the viewport phone-sized" check. Defaults to the 639px breakpoint
 * the sheets already use. Use it to gate `useScrollLock` so dominating-on-mobile
 * sheets/dropdowns (e.g. the notification sheet) stay scroll-through on desktop.
 */
export function useIsMobile(maxWidth = 639): boolean {
  const query = `(max-width: ${maxWidth}px)`;
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(query);
    const onChange = () => setIsMobile(mq.matches);
    setIsMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);

  return isMobile;
}
